import { combineLatest, Observable, Subject } from 'rxjs';
import { map, tap, filter, takeUntil, take } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy
} from '@angular/core';

import {
  loadGroups,
  createGroup,
  subscribeTo,
  unsubscribeFrom,
  unrequestGroup,
  resetGroups,
  loadOrgs
} from 'src/app/core/services/groups/groups.actions';
import {
  State as GroupsState,
  selectGroups,
  selectAmountOfPages,
  selectOrgs,
  selectAmountOfOrgsPages
} from 'src/app/core/services/groups/groups.reducer';
import { Group } from 'src/app/core/services/groups/group.model';

import { CreatePrivateGroupDialogComponent } from 'src/app/shared/components/create-private-group-dialog/create-private-group-dialog.component';
import { CreateSchoolClubChapterDialogComponent } from 'src/app/shared/components/create-school-club-chapter-dialog/create-school-club-chapter-dialog.component';
import { AuthService } from 'src/app/core/services/auth.service';
import { CompetitionsFacade } from 'src/app/modules/competitions/store';
import { NewCompetitionComponent } from 'src/app/modules/competitions/shared/dialogs/new-competition/new-competition.component';
import {
  CompetitionLinkService,
  GroupLinkService
} from 'src/app/shared/services/links';
import { GroupsFacade } from 'src/app/core/services/groups/groups.facade';
import { ProjectsFacade } from 'src/app/modules/projects/store';
import { AppClubLinkService } from 'src/app/shared/modules/links/services';
import { isNull } from 'util';

@Component({
  selector: 'app-groups-container',
  templateUrl: './groups-container.component.html',
  styleUrls: ['./groups-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GroupsFacade]
})
export class GroupsContainerComponent implements OnInit, OnDestroy {
  static readonly AMOUNT_OF_COMPETITIONS_PER_PAGE = 20;

  page = 1;
  pageOrg = 1;
  type: string;
  categoryID: number;

  selectedType: GroupsContainerComponent.Type;

  groups$: Observable<{
    items: Group[];
    totalCount: number;
    isLoading: boolean;
  }>;
  orgs$: Observable<{ items: Group[]; count: number }>;
  amountOfPages$: Observable<number>;
  amountOfOrgsPages$: Observable<number>;

  TYPE = GroupsContainerComponent.Type;

  private readonly componentDestroy$ = new Subject<void>();

  get type$() {
    return this.activatedRoute.data.pipe(map((_) => _.type));
  }

  get category() {
    return this.categoryID;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private groupsStore: Store<GroupsState>,
    private dialog: MatDialog,
    private auth: AuthService,
    private readonly projectsFacade: ProjectsFacade,
    private readonly groupsFacade: GroupsFacade,
    private readonly groupLinkService: GroupLinkService,
    private readonly appClubLinkService: AppClubLinkService,
    private readonly competitionsFacade: CompetitionsFacade,
    private readonly competitionLinkService: CompetitionLinkService
  ) {
    this.groups$ = this.groupsStore.pipe(select(selectGroups()));
    this.orgs$ = this.groupsStore.pipe(select(selectOrgs()));
    this.amountOfPages$ = this.groupsStore.pipe(select(selectAmountOfPages()));
    this.amountOfOrgsPages$ = this.groupsStore.pipe(
      select(selectAmountOfOrgsPages())
    );

    this.competitionsFacade.newCompetitionCreated$
      .pipe(
        takeUntil(this.componentDestroy$),
        tap((_) =>
          this.router.navigate(
            this.competitionLinkService.generateCompetitionLink(_)
          )
        )
      )
      .subscribe((_) => _);

    this.groupsFacade.newGroupCreated$
      .pipe(
        filter((_) => !_.is_club),
        tap((_) =>
          this.router.navigate(this.groupLinkService.generateGroupLink(_))
        ),
        takeUntil(this.componentDestroy$)
      )
      .subscribe();

    this.groupsFacade.newGroupCreated$
      .pipe(
        filter((_) => _.is_club),
        tap((_) =>
          this.router.navigate(this.appClubLinkService.generateLink(_))
        ),
        takeUntil(this.componentDestroy$)
      )
      .subscribe();
  }

  ngOnInit(): void {
    combineLatest([this.activatedRoute.queryParams, this.type$])
      .pipe(
        takeUntil(this.componentDestroy$),
        tap((_) => this.loadGroups(_[0]))
      )
      .subscribe((_) => _);
  }

  ngOnDestroy() {
    this.componentDestroy$.next();
    this.componentDestroy$.complete();
  }

  get isProjectsLoading$() {
    return this.projectsFacade.isLoading$;
  }

  get userType$() {
    return this.auth.userType$;
  }

  async onSetPage(page, isGroup: boolean) {
    await this.router.navigate([], {
      queryParams: {
        ...this.activatedRoute.snapshot.queryParams,
        [isGroup ? 'page' : 'pageOrg']: page
      }
    });
  }

  joinEventsGroup({ id, subscribe, undoJoin }) {
    if (undoJoin) {
      return this.groupsStore.dispatch(
        unrequestGroup({ group_id: id, user_id: this.auth.userID() })
      );
    }

    if (subscribe) {
      return this.groupsStore.dispatch(subscribeTo({ id }));
    }

    return this.groupsStore.dispatch(unsubscribeFrom({ id }));
  }

  onNewSchoolClubOrChapterGroup = () => {
    const dialogRef = this.dialog.open(CreateSchoolClubChapterDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(
        filter((res) => res && res.type === 'submit'),
        map((res) =>
          this.groupsStore.dispatch(createGroup({ group: res.payload }))
        )
      )
      .subscribe((_) => _);
  };

  onNewPrivateGroup = () => {
    const dialogRef = this.dialog.open(CreatePrivateGroupDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(
        filter((res) => res && res.type === 'submit'),
        map((res) =>
          this.groupsStore.dispatch(createGroup({ group: res.payload }))
        )
      )
      .subscribe((_) => _);
  };

  onSelectCategory(type: string) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { category: type },
      queryParamsHandling: 'merge'
    });
  }

  private async loadGroups(params) {
    this.selectedType =
      (await this.type$.pipe(take(1)).toPromise()) ||
      GroupsContainerComponent.Type.Clubs;
    this.type = this.parseGroupType(this.selectedType);

    const page = Number.isSafeInteger(Number(params.page))
      ? Number(params.page)
      : 1;
    const pageOrg = Number.isSafeInteger(Number(params.pageOrg))
      ? Number(params.pageOrg)
      : 1;
    const categoryID = this.parseCategory(params.category);

    this.page = page;
    this.pageOrg = pageOrg;
    this.categoryID = categoryID;

    if (this.type === 'clubs') {
      if (!this.categoryID) {
        this.groupsStore.dispatch(resetGroups());
      } else {
        this.groupsStore.dispatch(
          loadGroups({ typ: this.type, page, categoryID: this.categoryID })
        );
        this.groupsStore.dispatch(
          loadOrgs({ page: pageOrg, categoryID: this.categoryID })
        );
      }

      return;
    }

    this.groupsStore.dispatch(
      loadGroups({
        typ: this.type,
        page,
        weeklyStartFrom: params.weekly_start_from
      })
    );
  }

  private parseGroupType(type: GroupsContainerComponent.Type): string {
    switch (type) {
      case GroupsContainerComponent.Type.Clubs:
        return 'clubs';
      case GroupsContainerComponent.Type.SubscribedClubs:
        return 'subscribed-clubs';
      case GroupsContainerComponent.Type.Private:
        return 'private';
      case GroupsContainerComponent.Type.SubscribedPrivate:
        return 'subscribed-private';
      default:
        return 'clubs';
    }
  }

  private parseCategory(category: string): number {
    switch (category) {
      case 'science':
        return 1;
      case 'business':
        return 2;
      case 'community-service':
        return 3;
      case 'humanities':
        return 4;
      case 'social-sciences':
        return 5;
      case 'other':
        return 6;
      default:
        return null;
    }
  }

  protected readonly isNull = isNull;
}

export namespace GroupsContainerComponent {
  export enum Type {
    Clubs,
    SubscribedClubs,
    Private,
    SubscribedPrivate
  }
}
